import React, { useState, useEffect, useMemo } from "react";
import Page from "../shared/components/PageComponent";
import Layout from "../shared/components/LayoutComponent";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SearchTextField } from "../lib";
import { useTranslation } from "react-i18next";
import apiRequest from "../../functions/api";
import { useMessage } from "../../context/message-context";

// Icons
import { FilterList } from "@mui/icons-material";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

const program = "KMPAPI";

function KabelSuche(props) {
  const { accessToken, client } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showErrorMessage } = useMessage();

  // Local Storage
  const storedFilterObject = JSON.parse(localStorage.getItem("KabelSucheFilter"));
  const [deviceFilter, setDeviceFilter] = useState(storedFilterObject?.deviceFilter ? storedFilterObject?.deviceFilter : []);
  const [idFilter, setIdFilter] = useState(storedFilterObject?.idFilter ? storedFilterObject?.idFilter : []);

  // Vars
  const [filterDialog, setFilterDialog] = useState(false);
  const [suche, setSuche] = useState("");
  const [kabel, setKabel] = useState([]);
  const [filteredKabel, setFilteredKabel] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const deviceOptions = useMemo(() => {
    // Kombinieren von startGeraet und zielGeraet
    const allGeraete = kabel
      ?.map((mapElement) => [mapElement.startGeraet, mapElement.zielGeraet])
      .flat()
      ?.filter((item) => item !== null && item !== undefined);

    // Entfernen von Duplikaten
    const uniqueGeraete = [...new Set(allGeraete)];

    // Erstellen der Optionen
    return uniqueGeraete.map((geraet) => {
      return {
        value: geraet,
        label: geraet,
      };
    });
  }, [kabel]);

  const idOptions = useMemo(() => {
    // Herrausfiltern der einzelnen Optionen
    const tmpfilter = kabel
      ?.filter((value, index, array) => array.findIndex((element) => element.ID === value.ID) === index)
      .map((mapElement) => {
        return {
          value: mapElement.ID,
          label: mapElement.ID,
        };
      })
      ?.filter((item) => item !== null);
    return tmpfilter ? tmpfilter : [];
  }, [kabel]);

  // ----------------------------------------------------
  // -----------Allgemeine funktionen--------------------
  // ----------------------------------------------------
  useEffect(() => {
    setIsLoading(true);
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      statement: "KABEL_HOLEN",
    };

    apiRequest("POST", "daten", payload, headers, datenCb, errorCb);
  }, []);

  useEffect(() => {
    const result = kabel?.filter((item) => {
      const tmpDevices = deviceFilter?.length !== 0 ? deviceFilter?.some((searchItem) => [item.startGeraet, item.zielGeraet].includes(searchItem.label)) : true;

      const tmpId = idFilter?.length !== 0 ? idFilter?.some((searchItem) => searchItem.label === item.ID) : true;

      const tmpSuche =
        suche?.trim() !== "" ? [item.ID, item.startGeraet, item.zielGeraet].some((field) => field?.toLowerCase().includes(suche.toLowerCase())) : true;
      return tmpSuche && tmpDevices && tmpId;
    });

    setFilteredKabel(result);
  }, [kabel, suche, deviceFilter, idFilter]);

  useEffect(() => {
    const filterObject = {
      deviceFilter: deviceFilter,
      idFilter: idFilter,
    };
    localStorage.setItem("KabelSucheFilter", JSON.stringify(filterObject));
  }, [deviceFilter, idFilter]);

  const datenCb = (response) => {
    setIsLoading(false);
    if (response.status === 200) {
      setKabel(response.data.kabel);
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const errorCb = () => {
    showErrorMessage(t("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const rowClick = (id) => {
    navigate(`/KMPAPI/kabel/${id}`);
  };

  const resetFilter = () => {
    setSuche("");
    setDeviceFilter([]);
    setIdFilter([]);
  };

  // ----------------------------------------------------
  // -------------Handy funktionen-----------------------
  // ----------------------------------------------------
  const Main = () => {
    return (
      <>
        <Box sx={{ paddingTop: 2 }}>
          {filteredKabel && (
            <Box>
              <TableContainer sx={{ maxHeight: window.innerHeight - 250 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography>ID</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>Start</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>Ziel</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading && (
                      <TableRow key={"loading"}>
                        <TableCell align="center" colSpan={3}>
                          <CircularProgress size={24}></CircularProgress>
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading &&
                      filteredKabel?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
                        return (
                          <TableRow onClick={() => rowClick(item.ID)} key={item.ID}>
                            <TableCell>{item.ID}</TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography>{item.startGeraet}</Typography>
                                <Typography>{item.startPort}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography>{item.zielGeraet}</Typography>
                                <Typography>{item.zielPort}</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredKabel?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage=""
              />
            </Box>
          )}

          {!filteredKabel && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 240,
                color: "text.secondary",
                textAlign: "center",
                px: 2,
              }}
            >
              <InboxOutlinedIcon
                sx={{
                  fontSize: 64,
                  color: "action.disabled",
                  mb: 2,
                }}
              />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t("Keine Kabel gefunden")}
              </Typography>

              <Typography variant="body2" sx={{ maxWidth: 320 }}>
                {t("Es wurden noch keine Kabel angelegt. Legen Sie zunächst ein Kabel an, damit hier Ergebnisse angezeigt werden können.")}
              </Typography>
            </Box>
          )}
        </Box>
      </>
    );
  };

  return (
    <Page>
      <Layout>
        {/* Desktop */}
        <Box sx={{ display: { xs: "none", sm: "none", md: "block" }, width: "100%" }}>
          {/* Filter */}
          <Grid container justifyContent="center" spacing={1} style={{ paddingTop: 20 }}>
            <Grid item xs={12} md={1}>
              <Tooltip title={t("Zurück zur Hauptauswahl")}>
                <IconButton color="primary" onClick={() => navigate("/KMPAPI/")}>
                  <ReplyAllIcon />
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs={12} md={4}>
              <SearchTextField
                size="small"
                fullWidth
                label={t("Suche")}
                id="filter"
                variant="outlined"
                value={suche}
                inputProps={{
                  autoCapitalize: "off",
                  autoCorrect: "off",
                  spellCheck: "false",
                  autoComplete: "off",
                }}
                onChange={(e) => setSuche(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                multiple
                onChange={(event, newValue) => {
                  setIdFilter(newValue);
                }}
                value={idFilter}
                size="small"
                id="tags-outlined"
                options={idOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => <TextField {...params} label="Nach ID filtern" placeholder="ID Filter" />}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                multiple
                onChange={(event, newValue) => {
                  setDeviceFilter(newValue);
                }}
                value={deviceFilter}
                size="small"
                id="tags-outlined"
                options={deviceOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => <TextField {...params} label="Nach Gerät filtern" placeholder="Gerät Filter" />}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <IconButton color="error" onClick={() => resetFilter()}>
                <FilterListOffIcon />
              </IconButton>
            </Grid>
          </Grid>

          <Main />
        </Box>

        {/* Mobile */}
        <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
          {/* Filter */}
          <Box sx={{ paddingTop: 2 }} display="flex" alignItems={"center"}>
            <Tooltip title={t("Zurück zur Hauptauswahl")}>
              <IconButton color="primary" onClick={() => navigate("/KMPAPI/")}>
                <ReplyAllIcon />
              </IconButton>
            </Tooltip>

            <SearchTextField
              size="small"
              fullWidth
              label={t("Suche")}
              id="filter"
              variant="outlined"
              sx={{ marginLeft: "16px" }}
              value={suche}
              inputProps={{
                autoCapitalize: "off",
                autoCorrect: "off",
                spellCheck: "false",
                autoComplete: "off",
              }}
              onChange={(e) => setSuche(e.target.value)}
            />

            <Box pr={1}>
              <Tooltip title="Filter">
                <IconButton color="primary" onClick={() => setFilterDialog(true)}>
                  <FilterList />
                </IconButton>
              </Tooltip>
            </Box>
            <Box pr={1}>
              <Tooltip title="Filter entfernen">
                <IconButton color="error" onClick={() => resetFilter()}>
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Dialog open={filterDialog} onClose={() => setFilterDialog(false)} maxWidth="md" fullWidth>
              <DialogTitle>Filter</DialogTitle>
              <DialogContent>
                <Grid container justifyContent="center" spacing={1} style={{ paddingTop: 20 }}>
                  <Grid item xs={12} md={5}></Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => {
                        setIdFilter(newValue);
                      }}
                      value={idFilter}
                      size="small"
                      id="tags-outlined"
                      options={idOptions}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Nach ID filtern" placeholder="ID Filter" />}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => {
                        setDeviceFilter(newValue);
                      }}
                      value={deviceFilter}
                      size="small"
                      id="tags-outlined"
                      options={deviceOptions}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Nach Gerät filtern" placeholder="Gerät Filter" />}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button style={{ marginRight: "10px" }} color="primary" onClick={() => setFilterDialog(false)}>
                  {t("Fertig")}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          <Main />
        </Box>
      </Layout>
    </Page>
  );
}

export default KabelSuche;
